import { ConfigService } from '@axo/config/ConfigService';
import { NumberFieldUI } from '@axo/form/feature/form-input/NumberField/NumberFieldUI';
import { DebtType, Purpose } from '@axo/shared/data-access/types';
import { useTranslation } from '@axo/shared/i18n';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAxoFormContext } from '../../../../../../context/AxoForm/AxoForm';
import { useBroker } from '../../../../../../hooks/useBroker';
import { Currency } from '../../../../../../utils/Currency.format';
import { formatSpacedNumber } from '../../../../../../utils/format';
import {
  PurposeFormGroupData,
  SwedenFormData,
} from '../../../FormData.se.types';
import { PurposeField } from '../../../fields/PurposeField';
import { InitialPurpose } from '../../../fields/PurposeField/InitialPurposeField';
import {
  getRefinanceAmountSuccessValidation,
  unspecifiedDebtFieldOptions,
  unspecifiedDebtMonthlyDebtPaymentFieldOptions,
} from './config';

export const PurposeFormGroup = () => {
  const { t } = useTranslation();

  const broker = useBroker();
  const hasUnspecifiedDebtMonthlyDebtPayment = ConfigService.get(
    'product-unsecured-loan'
  ).fields[broker]?.hasUnspecifiedDebtMonthlyDebtPayment;

  const { axoFormData } = useAxoFormContext<SwedenFormData>();

  const axoAppliedAmount = axoFormData['LoanApplication/AppliedAmount'] ?? 0;

  const { watch, register } = useFormContext();

  const [appliedAmount, initialPurpose] = watch([
    'LoanApplication/AppliedAmount',
    'LoanApplication/InitialPurpose',
  ]);

  const askPurpose = initialPurpose && initialPurpose === Purpose.Other;
  const askRefinanceAmount =
    initialPurpose && initialPurpose === Purpose.RefinancingLoan;

  const refinanceAmountSuccessValidation = useMemo(
    () => getRefinanceAmountSuccessValidation(appliedAmount),
    [appliedAmount]
  );

  return (
    <>
      <InitialPurpose />

      {askPurpose && <PurposeField />}

      {askRefinanceAmount && (
        <>
          <input
            type="hidden"
            {...register('Debt/Unspecified/Type', { shouldUnregister: true })}
            value={DebtType.Unspecified}
          />

          <NumberFieldUI<PurposeFormGroupData>
            name="Debt/Unspecified/RefinanceAmount"
            defaultFromField={'LoanApplication/AppliedAmount'}
            label={t('Debt.Unspecified.label', {
              appliedAmount: Currency.format(appliedAmount || axoAppliedAmount),
            })}
            data-testid="debt-unspecified-refinance-amount-input"
            format={formatSpacedNumber}
            suffix={t('suffix.currency')}
            successValidation={refinanceAmountSuccessValidation}
            {...unspecifiedDebtFieldOptions}
          />

          {hasUnspecifiedDebtMonthlyDebtPayment && (
            <NumberFieldUI<PurposeFormGroupData>
              name="Debt/Unspecified/MonthlyDebtPayment"
              label={t('Debt.Unspecified.MonthlyDebtPayment.label')}
              data-testid="debt-unspecified-monthly-debt-payment-input"
              format={formatSpacedNumber}
              suffix={t('suffix.currency')}
              {...unspecifiedDebtMonthlyDebtPaymentFieldOptions}
            />
          )}
        </>
      )}
    </>
  );
};
